import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Button, IconButton, Paper, Dialog, DialogTitle, DialogContent,
  DialogActions, Tooltip, TextField, InputAdornment, CircularProgress, Alert, Grid, Card,
  CardContent, CardActions, Breadcrumbs, Link, Menu, MenuItem, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, ToggleButtonGroup, ToggleButton, Checkbox,
  Select, FormControl, InputLabel
} from '@mui/material';
import { Upload, Eye, Download, Trash2, FolderPlus, Edit2, MoreVertical, ChevronRight, Home, List, Grid3x3, Move } from 'lucide-react';
import { FaFolder, FaFolderOpen, FaFileWord, FaFileExcel, FaFilePdf, FaFileImage, FaFilePowerpoint, FaFile } from 'react-icons/fa';
import Page from 'src/components/Page';
import UDocViewer from 'src/components/MediaViewer/UDocViewer';
import apiInstance from 'src/utils/apiAuth';
import * as constants from 'src/utils/constants';
import { showToast, showErrorToast } from 'src/utils/Toast/SnackbarProvider';
import UserInfo from 'src/utils/Authorization/UserInfo';
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { getDDMMYYYYFormat } from 'src/utils/commonUtils';
import spotlightImage from 'src/assets/image.png';

export default function Templates() {
  const userInfo = UserInfo();
  
  // State management
  const [hierarchy, setHierarchy] = useState({ folders: [], files: [] });
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([{ id: null, name: 'Root' }]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState('list'); // 'grid' or 'list'
  
  // Selection state for bulk operations
  const [selectedItems, setSelectedItems] = useState({ folders: [], files: [] });
  const [bulkMoveDialog, setBulkMoveDialog] = useState({ open: false });
  const [moveDestination, setMoveDestination] = useState(null);
  
  // Dialog states
  const [createFolderDialog, setCreateFolderDialog] = useState({ open: false, parentId: null });
  const [editFolderDialog, setEditFolderDialog] = useState({ open: false, folder: null });
  const [deleteFolderDialog, setDeleteFolderDialog] = useState({ open: false, folderId: null });
  const [deleteFileDialog, setDeleteFileDialog] = useState({ open: false, fileId: null });
  const [viewDialog, setViewDialog] = useState({ open: false, template: null });
  const [uploadDialog, setUploadDialog] = useState({ open: false, folderId: null });
  
  // Form states
  const [folderName, setFolderName] = useState('');
  const [editFolderName, setEditFolderName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState('');
  
  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuItem, setMenuItem] = useState(null);
  const [menuType, setMenuType] = useState(null); // 'folder' or 'file'

  const TEMPLATES_URL = 'templates/';
  const FOLDERS_URL = 'template-folders/';

  // Fetch hierarchy on mount
  useEffect(() => {
    fetchHierarchy();
  }, []);


  // Fetch folder hierarchy
  const fetchHierarchy = async () => {
    try {
      setLoading(true);
      const { data } = await apiInstance.get(`${FOLDERS_URL}hierarchy`);
      setHierarchy(data);
      
      // Update currentFolder to point to the new data if we're inside a folder
      if (currentFolder) {
        const updatedFolder = findFolderById(data, currentFolder.id);
        if (updatedFolder) {
          setCurrentFolder(updatedFolder);
        }
      }
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to load folders', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Navigate into a folder
  const openFolder = (folder) => {
    setCurrentFolder(folder);
    setBreadcrumbs([...breadcrumbs, { id: folder.id, name: folder.name }]);
  };

  // Navigate to a specific breadcrumb
  const navigateTo = (index) => {
    const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
    setBreadcrumbs(newBreadcrumbs);
    
    if (index === 0) {
      setCurrentFolder(null);
    } else {
      // Find folder by traversing hierarchy
      const folderId = newBreadcrumbs[newBreadcrumbs.length - 1].id;
      const folder = findFolderById(hierarchy, folderId);
      setCurrentFolder(folder);
    }
  };

  // Helper to find folder by ID in hierarchy
  const findFolderById = (data, folderId) => {
    for (const folder of data.folders || []) {
      if (folder.id === folderId) return folder;
      const found = findFolderById(folder, folderId);
      if (found) return found;
    }
    return null;
  };

  // Get current view (folders and files at current level)
  const getCurrentView = () => {
    if (!currentFolder) {
      return hierarchy;
    }
    return currentFolder;
  };

  // Create folder
  const handleCreateFolder = async () => {
    if (!folderName.trim()) {
      showToast('Please enter a folder name', 'error');
      return;
    }

    try {
      const payload = {
        name: folderName,
        parent: createFolderDialog.parentId
      };

      await apiInstance.post(FOLDERS_URL, payload);
      showToast('Folder created successfully', 'success');
      setCreateFolderDialog({ open: false, parentId: null });
      setFolderName('');
      await fetchHierarchy();
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to create folder', 'error');
    }
  };

  // Edit folder
  const handleEditFolder = async () => {
    if (!editFolderName.trim()) {
      showToast('Please enter a folder name', 'error');
      return;
    }

    try {
      const payload = {
        name: editFolderName,
        parent: editFolderDialog.folder.parent
      };

      await apiInstance.put(`${FOLDERS_URL}${editFolderDialog.folder.id}/`, payload);
      showToast('Folder updated successfully', 'success');
      setEditFolderDialog({ open: false, folder: null });
      setEditFolderName('');
      await fetchHierarchy();
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to update folder', 'error');
    }
  };

  // Delete folder
  const handleDeleteFolder = async () => {
    try {
      await apiInstance.delete(`${FOLDERS_URL}${deleteFolderDialog.folderId}/`);
      showToast('Folder deleted successfully', 'success');
      setDeleteFolderDialog({ open: false, folderId: null });
      await fetchHierarchy();
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to delete folder', 'error');
    }
  };

  // Upload file
  const handleUpload = async () => {
    if (!selectedFile) {
      showToast('Please select a file', 'error');
      return;
    }

    try {
      const derivedTitle = (uploadTitle?.trim() ||
        (selectedFile.name ? selectedFile.name.replace(/\.[^/.]+$/, '') : 'Untitled'))
        .slice(0, 255);

      const form = new FormData();
      form.append('title', derivedTitle);
      form.append('file', selectedFile);
      if (uploadDialog.folderId) {
        form.append('folder', uploadDialog.folderId);
      }

      await apiInstance.post(TEMPLATES_URL, form);
      showToast('File uploaded successfully', 'success');
      setUploadDialog({ open: false, folderId: null });
      setSelectedFile(null);
      setUploadTitle('');
      await fetchHierarchy();
    } catch (err) {
      showToast(err?.response?.data?.message || 'Error uploading file', 'error');
    }
  };

  // Delete file
  const handleDeleteFile = async () => {
    try {
      await apiInstance.delete(`${TEMPLATES_URL}${deleteFileDialog.fileId}/`);
      showToast('File deleted successfully', 'success');
      setDeleteFileDialog({ open: false, fileId: null });
      await fetchHierarchy();
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to delete file', 'error');
    }
  };

  // Selection handlers
  const handleSelectFolder = (folderId) => {
    setSelectedItems(prev => {
      const isSelected = prev.folders.includes(folderId);
      return {
        ...prev,
        folders: isSelected 
          ? prev.folders.filter(id => id !== folderId)
          : [...prev.folders, folderId]
      };
    });
  };

  const handleSelectFile = (fileId) => {
    setSelectedItems(prev => {
      const isSelected = prev.files.includes(fileId);
      return {
        ...prev,
        files: isSelected 
          ? prev.files.filter(id => id !== fileId)
          : [...prev.files, fileId]
      };
    });
  };

  const handleSelectAll = () => {
    const currentView = getCurrentView();
    const allFolderIds = currentView.folders?.map(f => f.id) || [];
    const allFileIds = currentView.files?.map(f => f.id) || [];
    
    const allSelected = 
      selectedItems.folders.length === allFolderIds.length &&
      selectedItems.files.length === allFileIds.length;
    
    if (allSelected) {
      setSelectedItems({ folders: [], files: [] });
    } else {
      setSelectedItems({ folders: allFolderIds, files: allFileIds });
    }
  };

  const clearSelection = () => {
    setSelectedItems({ folders: [], files: [] });
  };

  // Bulk move handler
  const handleBulkMove = async () => {
    if (selectedItems.folders.length === 0 && selectedItems.files.length === 0) {
      showToast('Please select items to move', 'error');
      return;
    }

    try {
      const payload = {
        folder_ids: selectedItems.folders,
        file_ids: selectedItems.files,
        new_parent_id: moveDestination
      };

      await apiInstance.post(`${FOLDERS_URL}bulk-move/`, payload);
      showToast('Items moved successfully', 'success');
      setBulkMoveDialog({ open: false });
      setMoveDestination(null);
      clearSelection();
      await fetchHierarchy();
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to move items', 'error');
    }
  };

  // Get all folders for destination picker (excluding selected folders)
  const getAllFolders = (folders = hierarchy.folders, path = []) => {
    let result = [];
    folders.forEach(folder => {
      if (!selectedItems.folders.includes(folder.id)) {
        result.push({ ...folder, path: [...path, folder.name].join(' / ') });
        if (folder.folders && folder.folders.length > 0) {
          result = [...result, ...getAllFolders(folder.folders, [...path, folder.name])];
        }
      }
    });
    return result;
  };



  // Menu handlers
  const handleMenuOpen = (event, item, type) => {
    setAnchorEl(event.currentTarget);
    setMenuItem(item);
    setMenuType(type);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuItem(null);
    setMenuType(null);
  };

  const getExtFromUrl = (url) => {
    const clean = (url || '').split('?')[0].split('#')[0];
    const parts = clean.split('.');
    return parts.length > 1 ? parts.pop().toLowerCase() : '';
  };

  const makeDownloadName = (t) => {
    const base = (t.title || 'template').replace(/[^\w.-]+/g, '_');
    const ext = getExtFromUrl(t.file);
    return ext ? `${base}.${ext}` : base;
  };

const handleDownload = async (t) => {
    try {
      const response = await apiInstance.get(
        `${TEMPLATES_URL}${t.id}/download/`,
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = makeDownloadName(t);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showErrorToast('Failed to download file');
    }
  };

  const toHttps = (url) => url?.startsWith('http://') ? url.replace('http://', 'https://') : url;

  const [viewerError, setViewerError] = useState(false);
  const [currentViewer, setCurrentViewer] = useState(0);
  const [iframeLoading, setIframeLoading] = useState(true);

  const getViewers = (fileUrl, ext) => {
    const safeUrl = toHttps(fileUrl);
    const viewers = [];

    // PDF - docMentis primary, direct view fallback
    if (ext === 'pdf') {
      viewers.push(
        { type: 'component', src: safeUrl, name: 'docMentis' },
        { type: 'iframe', url: safeUrl, name: 'Direct PDF' }
      );
      return viewers;
    }

    // Images - direct view
    if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'bmp'].includes(ext)) {
      viewers.push({ type: 'iframe', url: safeUrl, name: 'Direct Image' });
      return viewers;
    }

    // Excel files - docMentis (if supported) then iframe fallbacks
    if (['xls', 'xlsx'].includes(ext)) {
      viewers.push(
        { type: 'component', src: safeUrl, name: 'docMentis' },
        { type: 'iframe', url: `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(safeUrl)}`, name: 'Microsoft Office' },
        { type: 'iframe', url: `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(safeUrl)}`, name: 'Google Docs' }
      );
      return viewers;
    }

    // Word files
    if (['doc', 'docx'].includes(ext)) {
      viewers.push(
        { type: 'component', src: safeUrl, name: 'docMentis' },
        { type: 'iframe', url: `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(safeUrl)}`, name: 'Google Docs' },
        { type: 'iframe', url: `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(safeUrl)}`, name: 'Microsoft Office' }
      );
      return viewers;
    }

    // PowerPoint files
    if (['ppt', 'pptx'].includes(ext)) {
      viewers.push(
        { type: 'component', src: safeUrl, name: 'docMentis' },
        { type: 'iframe', url: `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(safeUrl)}`, name: 'Google Docs' },
        { type: 'iframe', url: `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(safeUrl)}`, name: 'Microsoft Office' }
      );
      return viewers;
    }

    return [];
  };

  const handleView = (template) => {
    const ext = getExtFromUrl(template.file);
    const viewers = getViewers(template.file, ext);
    
    if (viewers.length > 0) {
      setViewerError(false);
      setCurrentViewer(0);
      setIframeLoading(true);
      setViewDialog({ open: true, template, viewers, ext });
    } else {
      handleDownload(template);
    }
  };

  const tryNextViewer = () => {
    if (currentViewer < (viewDialog.viewers?.length || 0) - 1) {
      setIframeLoading(true);
      setCurrentViewer(currentViewer + 1);
      setViewerError(false);
    } else {
      setViewerError(true);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).replace(/\//g, '-');
  };

  const FileIconComponent = ({ fileUrl }) => {
    const ext = getExtFromUrl(fileUrl);

    if (['doc', 'docx'].includes(ext)) {
      return <FaFileWord size={28} color="#2b579a" />;
    }
    if (['xls', 'xlsx'].includes(ext)) {
      return <FaFileExcel size={28} color="#217346" />;
    }
    if (['pdf'].includes(ext)) {
      return <FaFilePdf size={28} color="#d32f2f" />;
    }
    if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'bmp'].includes(ext)) {
      return <FaFileImage size={28} color="#ff9800" />;
    }
    if (['ppt', 'pptx'].includes(ext)) {
      return <FaFilePowerpoint size={28} color="#d24726" />;
    }
    return <FaFile size={28} color="#757575" />;
  };

  const currentView = getCurrentView();

  return (
    <Page title="Templates | CRM">
      <Container maxWidth="xl" sx={{ pt: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Templates
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<img src={spotlightImage} alt="GWX" style={{ width: 18, height: 18, objectFit: 'contain' }} />}
              onClick={() => window.open('https://thegatewaycorp.com/gwx-spotlight/', '_blank')}
              sx={{
                fontWeight: 600,
                borderRadius: "8px",
                borderColor: "#637381",
                color: "#212B36",
                "&:hover": { borderColor: "#212B36", backgroundColor: "rgba(145, 158, 171, 0.08)" }
              }}
            >
              Spotlight
            </Button>

            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newView) => newView && setViewMode(newView)}
              size="small"
              sx={{ height: 40 }}
            >
              <ToggleButton value="list" sx={{ px: 2 }}>
                <Tooltip title="List View">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <List size={18} />
                  </Box>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="grid" sx={{ px: 2 }}>
                <Tooltip title="Grid View">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Grid3x3 size={18} />
                  </Box>
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>

            {(selectedItems.folders.length > 0 || selectedItems.files.length > 0) && (
              <>
                <Chip 
                  label={`${selectedItems.folders.length + selectedItems.files.length} selected`}
                  onDelete={clearSelection}
                  color="primary"
                  sx={{ fontWeight: 600 }}
                />
                <Button
                  variant="contained"
                  startIcon={<Move size={18} />}
                  onClick={() => setBulkMoveDialog({ open: true })}
                  sx={{
                    backgroundColor: "#00AB55",
                    fontWeight: 700,
                    borderRadius: "8px",
                    "&:hover": { backgroundColor: "#007B55" }
                  }}
                >
                  Move
                </Button>
              </>
            )}

            <Button
              variant="outlined"
              startIcon={<FolderPlus size={18} />}
              onClick={() => setCreateFolderDialog({ open: true, parentId: currentFolder?.id || null })}
              sx={{
                fontWeight: 600,
                borderRadius: "8px",
                borderColor: "#637381",
                color: "#212B36",
                "&:hover": { borderColor: "#212B36", backgroundColor: "rgba(145, 158, 171, 0.08)" }
              }}
            >
              New Folder
            </Button>

            <Button
              variant="contained"
              startIcon={<Upload size={18} />}
              onClick={() => setUploadDialog({ open: true, folderId: currentFolder?.id || null })}
              sx={{
                backgroundColor: "#00AB55",
                fontWeight: 700,
                borderRadius: "8px",
                "&:hover": { backgroundColor: "#007B55" }
              }}
            >
              Upload File
            </Button>
          </Box>
        </Box>

        {/* Breadcrumbs */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Breadcrumbs separator={<ChevronRight size={16} />}>
            {breadcrumbs.map((crumb, index) => (
              <Link
                key={index}
                component="button"
                underline="hover"
                onClick={() => navigateTo(index)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: index === breadcrumbs.length - 1 ? 'text.primary' : 'text.secondary',
                  fontWeight: index === breadcrumbs.length - 1 ? 600 : 400,
                  cursor: 'pointer',
                  '&:hover': { color: 'primary.main' }
                }}
              >
                {index === 0 && <Home size={16} />}
                {crumb.name}
              </Link>
            ))}
          </Breadcrumbs>
        </Paper>

        {/* Content */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress />
          </Box>
        ) : viewMode === 'list' ? (
          /* List View - Windows Style */
          <TableContainer component={Paper}>
            <Table>
              <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={
                        (selectedItems.folders.length > 0 || selectedItems.files.length > 0) &&
                        (selectedItems.folders.length < (currentView.folders?.length || 0) ||
                         selectedItems.files.length < (currentView.files?.length || 0))
                      }
                      checked={
                        currentView.folders?.length > 0 &&
                        currentView.files?.length > 0 &&
                        selectedItems.folders.length === currentView.folders.length &&
                        selectedItems.files.length === currentView.files.length
                      }
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, width: '45%' }}>NAME</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: '20%' }}>TYPE</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: '20%' }}>DATE MODIFIED</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: '10%', textAlign: 'right' }}>ACTIONS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!currentView.folders?.length && !currentView.files?.length ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: 'center', py: 8 }}>
                      <FaFolder size={64} color="#ccc" />
                      <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
                        This folder is empty
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Create a folder or upload a file to get started
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {/* Folders */}
                    {currentView.folders?.map((folder) => (
                      <TableRow
                        key={`folder-${folder.id}`}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                          bgcolor: selectedItems.folders.includes(folder.id) ? 'rgba(0, 171, 85, 0.08)' : 'inherit'
                        }}
                        onDoubleClick={() => openFolder(folder)}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedItems.folders.includes(folder.id)}
                            onChange={() => handleSelectFolder(folder.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <FaFolder size={24} color="#FFA726" />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {folder.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label="Folder" 
                            size="small" 
                            sx={{ 
                              bgcolor: '#FFF3E0', 
                              color: '#E65100',
                              fontWeight: 500,
                              fontSize: '0.75rem'
                            }} 
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {getDDMMYYYYFormat(folder.modified || folder.created)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMenuOpen(e, folder, 'folder');
                            }}
                          >
                            <MoreVertical size={18} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}

                    {/* Files */}
                    {currentView.files?.map((file) => (
                      <TableRow
                        key={`file-${file.id}`}
                        sx={{ 
                          '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                          bgcolor: selectedItems.files.includes(file.id) ? 'rgba(0, 171, 85, 0.08)' : 'inherit'
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedItems.files.includes(file.id)}
                            onChange={() => handleSelectFile(file.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <FileIconComponent fileUrl={file.file} />
                            <Typography variant="body2">
                              {file.title}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                            {getExtFromUrl(file.file) || 'File'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {getDDMMYYYYFormat(file.modified || file.created)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, file, 'file')}
                          >
                            <MoreVertical size={18} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          /* Grid View */
          <Grid container spacing={2}>
            {/* Folders */}
            {currentView.folders?.map((folder) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={`folder-${folder.id}`}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    position: 'relative',
                    border: selectedItems.folders.includes(folder.id) ? '2px solid #00AB55' : '1px solid rgba(0,0,0,0.12)',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                  onDoubleClick={() => openFolder(folder)}
                >
                  <Checkbox
                    checked={selectedItems.folders.includes(folder.id)}
                    onChange={() => handleSelectFolder(folder.id)}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      zIndex: 1
                    }}
                  />
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <FaFolder size={40} color="#FFA726" />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {folder.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {folder.folders?.length || 0} folders, {folder.files?.length || 0} files
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuOpen(e, folder, 'folder');
                        }}
                      >
                        <MoreVertical size={18} />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}

            {/* Files */}
            {currentView.files?.map((file) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={`file-${file.id}`}>
                <Card
                  sx={{
                    transition: 'all 0.2s',
                    position: 'relative',
                    border: selectedItems.files.includes(file.id) ? '2px solid #00AB55' : '1px solid rgba(0,0,0,0.12)',
                    '&:hover': {
                      boxShadow: 4
                    }
                  }}
                >
                  <Checkbox
                    checked={selectedItems.files.includes(file.id)}
                    onChange={() => handleSelectFile(file.id)}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      zIndex: 1
                    }}
                  />
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <FileIconComponent fileUrl={file.file} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {file.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {file.uploaded_by_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {getDDMMYYYYFormat(file.created)}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, file, 'file')}
                      >
                        <MoreVertical size={18} />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}

            {/* Empty State */}
            {!currentView.folders?.length && !currentView.files?.length && (
              <Grid item xs={12}>
                <Paper sx={{ p: 8, textAlign: 'center' }}>
                  <FaFolder size={64} color="#ccc" />
                  <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
                    This folder is empty
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Create a folder or upload a file to get started
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        )}

        {/* Context Menu */}
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          {menuType === 'folder' && (
            <>
              <MenuItem onClick={() => { openFolder(menuItem); handleMenuClose(); }}>
                <FaFolderOpen size={16} style={{ marginRight: 8 }} /> Open
              </MenuItem>
              <MenuItem onClick={() => { setEditFolderDialog({ open: true, folder: menuItem }); setEditFolderName(menuItem.name); handleMenuClose(); }}>
                <Edit2 size={16} style={{ marginRight: 8 }} /> Rename
              </MenuItem>
              <MenuItem onClick={() => { setDeleteFolderDialog({ open: true, folderId: menuItem.id }); handleMenuClose(); }}>
                <Trash2 size={16} style={{ marginRight: 8 }} /> Delete
              </MenuItem>
            </>
          )}
          {menuType === 'file' && (
            <>
              <MenuItem onClick={() => { handleView(menuItem); handleMenuClose(); }}>
                <Eye size={16} style={{ marginRight: 8 }} /> View
              </MenuItem>
              <MenuItem onClick={() => { handleDownload(menuItem); handleMenuClose(); }}>
                <Download size={16} style={{ marginRight: 8 }} /> Download
              </MenuItem>
              {menuItem?.uploaded_by === userInfo?.pk && (
                <MenuItem onClick={() => { setDeleteFileDialog({ open: true, fileId: menuItem.id }); handleMenuClose(); }}>
                  <Trash2 size={16} style={{ marginRight: 8 }} /> Delete
                </MenuItem>
              )}
            </>
          )}
        </Menu>

        {/* Create Folder Dialog */}
        <Dialog open={createFolderDialog.open} onClose={() => { setCreateFolderDialog({ open: false, parentId: null }); setFolderName(''); }}>
          <DialogTitle>Create New Folder</DialogTitle>
          <DialogContent sx={{ width: 400 }}>
            <TextField
              autoFocus
              fullWidth
              label="Folder Name"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleCreateFolder()}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setCreateFolderDialog({ open: false, parentId: null }); setFolderName(''); }}>Cancel</Button>
            <Button onClick={handleCreateFolder} variant="contained">Create</Button>
          </DialogActions>
        </Dialog>

        {/* Edit Folder Dialog */}
        <Dialog open={editFolderDialog.open} onClose={() => { setEditFolderDialog({ open: false, folder: null }); setEditFolderName(''); }}>
          <DialogTitle>Rename Folder</DialogTitle>
          <DialogContent sx={{ width: 400 }}>
            <TextField
              autoFocus
              fullWidth
              label="Folder Name"
              value={editFolderName}
              onChange={(e) => setEditFolderName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleEditFolder()}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setEditFolderDialog({ open: false, folder: null }); setEditFolderName(''); }}>Cancel</Button>
            <Button onClick={handleEditFolder} variant="contained">Update</Button>
          </DialogActions>
        </Dialog>

        {/* Delete Folder Dialog */}
        <Dialog open={deleteFolderDialog.open} onClose={() => setDeleteFolderDialog({ open: false, folderId: null })}>
          <DialogTitle>Delete Folder</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this folder? All files and subfolders will also be deleted.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteFolderDialog({ open: false, folderId: null })}>Cancel</Button>
            <Button onClick={handleDeleteFolder} variant="contained" color="error">Delete</Button>
          </DialogActions>
        </Dialog>

        {/* Delete File Dialog */}
        <Dialog open={deleteFileDialog.open} onClose={() => setDeleteFileDialog({ open: false, fileId: null })}>
          <DialogTitle>Delete File</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this file?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteFileDialog({ open: false, fileId: null })}>Cancel</Button>
            <Button onClick={handleDeleteFile} variant="contained" color="error">Delete</Button>
          </DialogActions>
        </Dialog>

        {/* Upload Dialog */}
        <Dialog open={uploadDialog.open} onClose={() => { setUploadDialog({ open: false, folderId: null }); setSelectedFile(null); setUploadTitle(''); }}>
          <DialogTitle>Upload File</DialogTitle>
          <DialogContent sx={{ width: 400 }}>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{ mt: 2, mb: 2 }}
            >
              {selectedFile ? selectedFile.name : 'Choose File'}
              <input
                type="file"
                hidden
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
              />
            </Button>
            <TextField
              fullWidth
              label="Title (optional)"
              value={uploadTitle}
              onChange={(e) => setUploadTitle(e.target.value)}
              helperText="Leave empty to use filename"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setUploadDialog({ open: false, folderId: null }); setSelectedFile(null); setUploadTitle(''); }}>Cancel</Button>
            <Button onClick={handleUpload} variant="contained" disabled={!selectedFile}>Upload</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={viewDialog.open}
          onClose={() => { setViewDialog({ open: false, template: null }); setViewerError(false); setCurrentViewer(0); setIframeLoading(true); }}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">{viewDialog.template?.title}</Typography>
              {viewDialog.viewers && viewDialog.viewers.length > 1 && (
                <Typography variant="caption" color="text.secondary">
                  Viewer: {viewDialog.viewers[currentViewer]?.name}
                </Typography>
              )}
            </Box>
          </DialogTitle>
          <DialogContent sx={{ height: '80vh', position: 'relative', p: 2 }}>
            {iframeLoading && !viewerError && (
              <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10, textAlign: 'center' }}>
                <CircularProgress size={50} />
                <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                  Loading document...
                </Typography>
              </Box>
            )}
            {viewerError ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Unable to preview this file. Please download it to view.
                </Alert>
                <Button 
                  variant="contained" 
                  onClick={() => handleDownload(viewDialog.template)}
                  startIcon={<Download size={18} />}
                >
                  Download File
                </Button>
              </Box>
            ) : (
              viewDialog.viewers?.[currentViewer] && (
                viewDialog.viewers[currentViewer].type === 'component' ? (
                  <div key={currentViewer} style={{ width: '100%', height: '100%', opacity: iframeLoading ? 0 : 1, transition: 'opacity 0.3s' }}>
                    <UDocViewer
                      src={viewDialog.viewers[currentViewer].src}
                      onLoad={() => setIframeLoading(false)}
                      onError={tryNextViewer}
                    />
                  </div>
                ) : (
                  <iframe
                    key={currentViewer}
                    src={viewDialog.viewers[currentViewer].url}
                    style={{ width: '100%', height: '100%', border: 'none', opacity: iframeLoading ? 0 : 1, transition: 'opacity 0.3s' }}
                    title={viewDialog.template?.title}
                    onLoad={() => setIframeLoading(false)}
                    onError={tryNextViewer}
                  />
                )
              )
            )}
          </DialogContent>
          <DialogActions>
            {viewDialog.viewers && currentViewer < viewDialog.viewers.length - 1 && !viewerError && (
              <Button onClick={tryNextViewer} color="warning">
                Try Alternative Viewer
              </Button>
            )}
            <Button onClick={() => { setViewDialog({ open: false, template: null }); setViewerError(false); setCurrentViewer(0); setIframeLoading(true); }}>Close</Button>
            <Button 
              onClick={() => handleDownload(viewDialog.template)}
              startIcon={<Download size={18} />}
              variant="contained"
            >
              Download
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bulk Move Dialog */}
        <Dialog 
          open={bulkMoveDialog.open} 
          onClose={() => {
            setBulkMoveDialog({ open: false });
            setMoveDestination(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Move Selected Items</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Moving {selectedItems.folders.length} folder(s) and {selectedItems.files.length} file(s)
            </Typography>
            
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Destination Folder</InputLabel>
              <Select
                value={moveDestination === null ? 'root' : moveDestination}
                onChange={(e) => setMoveDestination(e.target.value === 'root' ? null : e.target.value)}
                label="Destination Folder"
              >
                <MenuItem value="root">
                  <em>Root Folder</em>
                </MenuItem>
                {getAllFolders().map((folder) => (
                  <MenuItem key={folder.id} value={folder.id}>
                    {folder.path || folder.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedItems.folders.length > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Moving folders will also move all their contents
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => {
                setBulkMoveDialog({ open: false });
                setMoveDestination(null);
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleBulkMove} 
              variant="contained"
              color="primary"
            >
              Move Items
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}
